import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Typography,
  Grid,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { url } from '../../constants';

const steps = ['Account Details', 'Select Preferences', 'Complete'];

// Image compression utility
const compressImage = (file, maxSizeKB = 100) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Resize if too large (max 800px)
        const MAX_DIMENSION = 800;
        if (width > height) {
          if (width > MAX_DIMENSION) {
            height *= MAX_DIMENSION / width;
            width = MAX_DIMENSION;
          }
        } else {
          if (height > MAX_DIMENSION) {
            width *= MAX_DIMENSION / height;
            height = MAX_DIMENSION;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.9;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);

        // Compress until under 100KB
        while (Math.round((dataUrl.length * 3) / 4) > maxSizeKB * 1024 && quality > 0.1) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }

        resolve(dataUrl);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const VendorRegister = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    ownerName: '',
    phone: '',
    businessType: '',
    selectedCategories: [],
    profile_image: null, // Added for image upload
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${url}/users/get_categories`);
        if (!response.ok) throw new Error('Failed to fetch categories');
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        setApiError('Failed to load categories. Please refresh the page.');
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (apiError) setApiError('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressedBase64 = await compressImage(file, 100);
      setFormData(prev => ({ ...prev, profile_image: compressedBase64 }));
      if (apiError) setApiError('');
    } catch (error) {
      console.error("Error compressing image:", error);
      setApiError("Failed to process the image. Please try another one.");
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email address';

    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 4) newErrors.password = 'Password must be at least 4 characters';

    if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    // no mandatory preference selection for vendor
    setErrors({});
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!validateStep1()) return;
    }
    if (activeStep === 1) {
      if (!validateStep2()) return;
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setApiError('');
    setSuccessMessage('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setApiError('');
    setSuccessMessage('');
    try {
      const payload = {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        business_name: formData.businessName || null,
        owner_name: formData.ownerName || null,
        phone: formData.phone || null,
        business_type: formData.businessType || null,
        categories: formData.selectedCategories,
        profile_image: formData.profile_image || null, // Sending the compressed image
      };
      const response = await fetch(`${url}/vendors/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.detail || 'Registration failed');

      setSuccessMessage('✅ Vendor account created! Redirecting to login...');
      
      setTimeout(() => {
        navigate('/vendor/login');
      }, 2000);
    } catch (error) {
      setApiError(error.message);
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600} sx={{ mb: 1 }}>
        Create Your Vendor Account
      </Typography>
      {apiError && <Alert severity="error" sx={{ mb: 3 }}>{apiError}</Alert>}
      <Grid container spacing={3}>
        
        {/* Profile Image Upload Section */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 1, mt: 1 }}>
            {formData.profile_image && (
              <Box
                component="img"
                src={formData.profile_image}
                alt="Profile Preview"
                sx={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '2px solid #1a7f3a' }}
              />
            )}
            
            <Button variant="outlined" component="label" sx={{ color: '#1a7f3a', borderColor: '#1a7f3a' }}>
              {formData.profile_image ? "Change Profile Image" : "Upload Profile Image"}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            placeholder="your.email@example.com"
            InputProps={{ startAdornment: <InputAdornment position="start"><Email /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            placeholder="Min 6 characters"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            placeholder="Re-enter your password"
          />
        </Grid>

        {/* Optional - business-related */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Business Name"
            name="businessName"
            value={formData.businessName}
            onChange={handleChange}
            placeholder="Optional"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Owner Name"
            name="ownerName"
            value={formData.ownerName}
            onChange={handleChange}
            placeholder="Optional"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Optional"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Business Type"
            name="businessType"
            value={formData.businessType}
            onChange={handleChange}
            placeholder="Optional"
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleNext}
          disabled={loading}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600} sx={{ mb: 1 }}>
        Select Your Interests
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose categories that match your business - helps improve deal targeting.
      </Typography>

      {errors.categories && <Alert severity="warning" sx={{ mb: 3 }}>{errors.categories}</Alert>}

      {categoriesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : categories.length === 0 ? (
        <Alert severity="info">No categories available right now.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {categories.map(category => {
            const id = category.cate_id;
            const selected = formData.selectedCategories.includes(id);
            return (
              <Button
                key={id}
                variant={selected ? 'contained' : 'outlined'}
                onClick={() => {
                  const newSelection = selected
                    ? formData.selectedCategories.filter(x => x !== id)
                    : [...formData.selectedCategories, id];
                  setFormData({ ...formData, selectedCategories: newSelection });
                }}
                sx={{
                  borderRadius: '20px',
                  px: 3,
                  py: 1.5,
                  bgcolor: selected ? category.cate_color : 'transparent',
                  borderColor: selected ? category.cate_color : '#ddd',
                  color: selected ? 'white' : 'text.primary',
                  fontWeight: 600,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: selected ? category.cate_color : 'action.hover',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {category.cate_name}
              </Button>
            );
          })}
        </Box>
      )}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          size="large"
          variant="outlined"
          onClick={handleBack}
          disabled={loading}
          sx={{ mr: 2, px: 5, py: 1.5 }}
        >
          Back
        </Button>
        <Button
          size="large"
          variant="contained"
          onClick={() => {
            // No required categories, so directly submit
            setActiveStep(activeStep + 1);
          }}
          disabled={loading}
          sx={{ px: 5, py: 1.5 }}
        >
          Finish
        </Button>
      </Box>
    </Box>
  );

  const renderStep3 = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Welcome!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Your vendor account setup is almost complete.
      </Typography>

      {successMessage && <Alert severity="success" sx={{ mb: 3 }}>{successMessage}</Alert>}

      {formData.selectedCategories.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Your Selected Categories:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
            {formData.selectedCategories.map(catId => {
              const cat = categories.find(c => c.cate_id === catId || c._id === catId);
              return (
                <Paper
                  key={catId}
                  sx={{
                    px: 2.5,
                    py: 1.2,
                    bgcolor: cat?.cate_color || '#999',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  {cat?.cate_name || 'Unknown'}
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
        size="large"
        sx={{ px: 5, py: 1.5 }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            Creating Account...
          </Box>
        ) : 'Create Account'}
      </Button>
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 400 }}>
          {activeStep === 0 && renderStep1()}
          {activeStep === 1 && renderStep2()}
          {activeStep === 2 && renderStep3()}
        </Box>
      </Paper>
    </Container>
  );
};

export default VendorRegister;